import { useState } from "react";
import { Link } from "react-router-dom";
import {
  SYSTEM_ACTOR,
  type ChangeAuditEntry,
  type ChangeAuditSource,
} from "@/api/changeAudit.service";
import { ROUTES } from "@/routes";
import styles from "./AuditLog.module.css";
import { researchActionLabel } from "./researchLabels";

export function formatWhen(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/** The email when we have one, else the raw actor; a system writer reads as "System · <source>". */
export function ActorCell({
  entry,
}: {
  entry: Pick<ChangeAuditEntry, "actor" | "actorEmail" | "actorType">;
}) {
  if (entry.actorEmail) {
    return (
      <span className={styles.actorEmail} title={entry.actorEmail}>
        {entry.actorEmail}
      </span>
    );
  }
  if (entry.actorType === "system") {
    const name = entry.actor.startsWith(SYSTEM_ACTOR)
      ? entry.actor.slice(SYSTEM_ACTOR.length)
      : entry.actor;
    return <span className={styles.actorSystem}>System · {name}</span>;
  }
  return (
    <span className={styles.actorEmail} title={entry.actor}>
      {entry.actor}
    </span>
  );
}

export function ActionPill({ action }: { action: string }) {
  return <span className={styles.actionPill}>{action}</span>;
}

export function actionLabel(source: ChangeAuditSource, action: string): string {
  return source === "research-cms" ? researchActionLabel(action) : action;
}

export function changedFieldNames(entry: ChangeAuditEntry): string[] {
  return entry.changes ? Object.keys(entry.changes) : [];
}

export const ENTITY_PAGE: Partial<
  Record<ChangeAuditSource, { label: string; path: (id: string) => string }>
> = {
  "approval-requests": { label: "approval request", path: ROUTES.research.contentInboxDetail },
  testimonials: { label: "testimonial", path: ROUTES.research.testimonialDetail },
};

/** First eight characters, linking to the record, with a button that copies the whole id. */
export function EntityIdCell({ id, source }: { id: string; source: ChangeAuditSource }) {
  const [copied, setCopied] = useState(false);

  async function copy(event: React.MouseEvent<HTMLButtonElement>) {
    // The row itself opens the detail, so the copy must not also open it.
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  const page = ENTITY_PAGE[source];

  return (
    <span className={styles.entityId}>
      {page ? (
        <Link
          to={page.path(id)}
          className={`${styles.entityLink} ${styles.mono}`}
          title={id}
          aria-label={`Open ${page.label} ${id}`}
          // The row itself opens the detail, so following the link must not also open it.
          onClick={event => event.stopPropagation()}
        >
          {id.slice(0, 8)}
        </Link>
      ) : (
        <span className={styles.mono} title={id}>
          {id.slice(0, 8)}
        </span>
      )}
      <button
        type="button"
        className={styles.linkButton}
        onClick={copy}
        aria-label={`Copy entity id ${id}`}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}
